<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">{{ isSelfService ? 'My Leave' : 'Leave Management' }}</h5>
        <p class="page-sub">{{ isSelfService ? 'Apply and track your leave requests' : 'Apply, approve and track employee leave' }}</p>
      </div>
      <div class="page-actions">
        <button v-if="!isSelfService" class="btn-outline" @click="activeTab = 'types'" :class="{ 'btn-active': activeTab === 'types' }">
          Leave Types
        </button>
        <button v-if="!isSelfService" class="btn-outline" @click="activeTab = 'requests'" :class="{ 'btn-active': activeTab === 'requests' }">
          Requests
        </button>
        <button class="btn-primary-action" @click="openApplyModal">
          <font-awesome-icon icon="plus" /> Apply Leave
        </button>
      </div>
    </div>

    <!-- ════════════════════  TAB: LEAVE TYPES  ════════════════════ -->
    <div v-if="activeTab === 'types'">
      <div class="section-header">
        <p class="section-title">Leave Types & Policies</p>
        <button class="btn-primary-action" @click="openCreateType">
          <font-awesome-icon icon="plus" /> New Type
        </button>
      </div>

      <div v-if="typesLoading" class="sk-table">
        <div v-for="n in 5" :key="n" class="sk-row"></div>
      </div>

      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Accrual</th>
              <th>Max Days</th>
              <th>Carry Fwd</th>
              <th>Notice</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lt in leaveTypes" :key="lt.id">
              <td class="fw-600">{{ lt.name }}</td>
              <td><span class="chip" :class="lt.is_paid ? 'chip--green' : 'chip--gray'">{{ lt.is_paid ? 'Paid' : 'Unpaid' }}</span></td>
              <td>{{ capitalize(lt.accrual_rule) }}</td>
              <td>{{ lt.max_days }} days</td>
              <td>{{ lt.carry_forward_limit > 0 ? lt.carry_forward_limit + ' days' : '—' }}</td>
              <td>{{ lt.min_notice_days > 0 ? lt.min_notice_days + ' days' : '—' }}</td>
              <td><span class="chip" :class="lt.is_active ? 'chip--blue' : 'chip--gray'">{{ lt.is_active ? 'Active' : 'Inactive' }}</span></td>
              <td>
                <button class="btn-icon" @click="openEditType(lt)"><font-awesome-icon icon="pen" /></button>
                <button class="btn-icon" @click="toggleTypeStatus(lt)"><font-awesome-icon :icon="lt.is_active ? 'toggle-on' : 'toggle-off'" /></button>
                <button class="btn-icon btn-icon--red" @click="deleteType(lt)"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!leaveTypes.length">
              <td colspan="8" class="empty-cell">No leave types configured</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ════════════════════  TAB: REQUESTS  ════════════════════ -->
    <div v-if="activeTab === 'requests'">
      <!-- Filters -->
      <div class="filter-bar">
        <select class="filter-select" v-model="reqFilters.status">
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select class="filter-select" v-model="reqFilters.leave_type_id">
          <option value="">All Types</option>
          <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">{{ lt.name }}</option>
        </select>
        <input type="date" class="filter-input" v-model="reqFilters.date_from" />
        <input type="date" class="filter-input" v-model="reqFilters.date_to" />
        <input v-if="!isSelfService" type="text" class="filter-input" v-model="reqFilters.search" placeholder="Search employee…" />
        <div class="per-page-wrap">
          <label class="per-page-label">Per page:</label>
          <select class="per-page-select" v-model.number="reqFilters.length" @change="reqFilters.page = 1; fetchRequests()">
            <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
        <button class="btn-primary-action" @click="applyFilters"><font-awesome-icon icon="search" /></button>
        <div class="export-wrap">
          <button class="btn-export" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" />
          </button>
          <div v-if="exportOpen" class="export-menu">
            <button class="export-item" @click="openExportModal('csv'); exportOpen = false">
              <font-awesome-icon icon="file-csv" /> Export CSV
            </button>
            <button class="export-item" @click="openExportModal('pdf'); exportOpen = false">
              <font-awesome-icon icon="file-pdf" /> Export PDF
            </button>
          </div>
        </div>
      </div>

      <div v-if="reqLoading" class="sk-table">
        <div v-for="n in 6" :key="n" class="sk-row"></div>
      </div>

      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th v-if="!isSelfService">Employee</th>
              <th>Leave Type</th>
              <th>From</th>
              <th>To</th>
              <th>Days</th>
              <th>Status</th>
              <th>Applied On</th>
              <th v-if="canCancelLeave"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="req in requests" :key="req.id">
              <td v-if="!isSelfService">
                <div class="emp-cell">
                  <span class="emp-name">{{ req.employee?.first_name }} {{ req.employee?.last_name }}</span>
                  <span class="emp-no">{{ req.employee?.employee_no }}</span>
                </div>
              </td>
              <td>{{ req.leave_type?.name }}</td>
              <td>{{ fmtDate(req.from_date) }}</td>
              <td>{{ fmtDate(req.to_date) }}</td>
              <td>{{ req.days }}</td>
              <td><span class="chip" :class="reqStatusClass(req.status)">{{ capitalize(req.status) }}</span></td>
              <td>{{ fmtDate(req.created_at) }}</td>
              <td v-if="canCancelLeave">
                <button
                  v-if="['pending','approved'].includes(req.status) && leaveNotStarted(req)"
                  class="btn-cancel-leave"
                  @click="cancelReq(req.id)"
                  title="Cancel leave"
                >
                  <font-awesome-icon icon="ban" /> Cancel
                </button>
              </td>
            </tr>
            <tr v-if="!requests.length">
              <td :colspan="isSelfService ? 6 : canCancelLeave ? 8 : 7" class="empty-cell">No leave requests found</td>
            </tr>
          </tbody>
        </table>
        <div class="pagination-bar" v-if="pagination && pagination.last_page > 1">
          <span class="pagination-info">
            Showing {{ (pagination.current_page - 1) * reqFilters.length + 1 }}–{{ Math.min(pagination.current_page * reqFilters.length, pagination.total) }} of {{ pagination.total }}
          </span>
          <div class="pagination-controls">
            <button class="pg-btn" :disabled="pagination.current_page === 1" @click="reqPage(1)">«</button>
            <button class="pg-btn" :disabled="pagination.current_page === 1" @click="reqPage(pagination.current_page - 1)">‹</button>
            <div class="pg-pages">
              <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.current_page }" @click="reqPage(p)">{{ p }}</button>
            </div>
            <button class="pg-btn" :disabled="pagination.current_page === pagination.last_page" @click="reqPage(pagination.current_page + 1)">›</button>
            <button class="pg-btn" :disabled="pagination.current_page === pagination.last_page" @click="reqPage(pagination.last_page)">»</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ Column picker modal ══ -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-action" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ Create / Edit Leave Type Modal ══ -->
    <div class="modal-overlay" v-if="typeModal.open" @click.self="typeModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <p class="modal-title">{{ typeModal.id ? 'Edit Leave Type' : 'New Leave Type' }}</p>
          <button class="modal-close" @click="typeModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Name <span class="req">*</span></label>
              <input type="text" class="form-input" v-model="typeModal.name" placeholder="e.g. Annual Leave" />
            </div>
            <div class="form-row">
              <label class="form-label">Type</label>
              <select class="form-input" v-model="typeModal.is_paid">
                <option :value="true">Paid</option>
                <option :value="false">Unpaid</option>
              </select>
            </div>
          </div>
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Accrual Rule</label>
              <select class="form-input" v-model="typeModal.accrual_rule">
                <option value="yearly">Yearly</option>
                <option value="monthly">Monthly</option>
                <option value="pro_rated">Pro-rated</option>
                <option value="none">None</option>
              </select>
            </div>
            <div class="form-row">
              <label class="form-label">Max Days / Year</label>
              <input type="number" class="form-input" v-model="typeModal.max_days" min="0" />
            </div>
          </div>
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Carry-forward Limit (days)</label>
              <input type="number" class="form-input" v-model="typeModal.carry_forward_limit" min="0" />
            </div>
            <div class="form-row">
              <label class="form-label">Carry-forward Deadline</label>
              <input type="date" class="form-input" v-model="typeModal.carry_forward_deadline" />
            </div>
          </div>
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Min Notice (days)</label>
              <input type="number" class="form-input" v-model="typeModal.min_notice_days" min="0" />
            </div>
            <div class="form-row">
              <label class="form-label">Eligibility (months service)</label>
              <input type="number" class="form-input" v-model="typeModal.eligibility_months" min="0" />
            </div>
          </div>
          <div class="form-cols">
            <div class="form-row form-row--check">
              <label class="check-label"><input type="checkbox" v-model="typeModal.allow_encashment" /> Allow Encashment</label>
            </div>
            <div class="form-row form-row--check">
              <label class="check-label"><input type="checkbox" v-model="typeModal.requires_attachment" /> Requires Attachment</label>
            </div>
          </div>
          <div class="form-row" v-if="typeModal.requires_attachment">
            <label class="form-label">Attachment Required After (days)</label>
            <input type="number" class="form-input" v-model="typeModal.attachment_required_after_days" min="0" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="typeModal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="saveType">
            {{ saving ? 'Saving…' : (typeModal.id ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ Apply Leave Modal ══ -->
    <div class="modal-overlay" v-if="applyModal.open" @click.self="applyModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Apply for Leave</p>
          <button class="modal-close" @click="applyModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div v-if="isSelfService && !employeeId" class="ss-no-profile">
            <font-awesome-icon icon="exclamation-triangle" />
            Your account is not linked to an employee profile. Contact HR to submit leave.
          </div>
          <div class="form-row" v-if="!isSelfService">
            <label class="form-label">Employee <span class="req">*</span></label>
            <select class="form-input" v-model="applyModal.employee_id" @change="loadBalances">
              <option value="">— select —</option>
              <option v-for="e in allEmployees" :key="e.id" :value="e.id">{{ e.first_name }} {{ e.last_name }} ({{ e.employee_no }})</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Leave Type <span class="req">*</span></label>
            <select class="form-input" v-model="applyModal.leave_type_id">
              <option value="">— select —</option>
              <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">{{ lt.name }} ({{ lt.is_paid ? 'Paid' : 'Unpaid' }})</option>
            </select>
          </div>
          <!-- Balance display -->
          <div v-if="applyModal.balances.length" class="balance-pills">
            <div v-for="b in applyModal.balances" :key="b.leave_type_id" class="balance-pill" :class="{ 'balance-pill--active': b.leave_type_id === applyModal.leave_type_id }">
              <span class="b-name">{{ b.leave_type?.name }}</span>
              <span class="b-val">{{ b.available }} days</span>
            </div>
          </div>
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">From <span class="req">*</span></label>
              <input type="date" class="form-input" v-model="applyModal.from_date" />
            </div>
            <div class="form-row">
              <label class="form-label">To <span class="req">*</span></label>
              <input type="date" class="form-input" v-model="applyModal.to_date" />
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Reason</label>
            <textarea class="form-input" rows="3" v-model="applyModal.reason" placeholder="Optional reason…"></textarea>
          </div>
          <div class="form-row">
            <label class="form-label">Attachment (medical cert, etc.)</label>
            <input type="file" class="form-input" @change="applyModal.attachment = $event.target.files[0]" accept=".jpg,.jpeg,.png,.pdf" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="applyModal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving || (isSelfService && !employeeId)" @click="submitLeave">
            {{ saving ? 'Submitting…' : 'Submit Request' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ Reject Modal ══ -->
    <div class="modal-overlay" v-if="rejectModal.open" @click.self="rejectModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Reject Leave Request</p>
          <button class="modal-close" @click="rejectModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Reason for rejection</label>
            <textarea class="form-input" rows="3" v-model="rejectModal.note"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="rejectModal.open = false">Cancel</button>
          <button class="btn-reject-action" :disabled="saving" @click="submitReject">
            {{ saving ? 'Rejecting…' : 'Reject Request' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'LeavePage',
  data() {
    return {
      activeTab: 'requests',
      saving: false,
      // Leave types
      leaveTypes: [], typesLoading: false,
      typeModal: { open: false, id: null, name: '', is_paid: true, accrual_rule: 'yearly', max_days: 20, carry_forward_limit: 0, carry_forward_deadline: '', allow_encashment: false, requires_attachment: false, attachment_required_after_days: 0, min_notice_days: 0, eligibility_months: 0 },
      // Requests
      requests: [], reqLoading: false,
      reqMeta: { current_page: 1, last_page: 1 },
      reqFilters: { status: '', leave_type_id: '', date_from: '', date_to: '', search: '', employee_id: '', length: 5, page: 1 },
      pagination: null,
      // Apply
      applyModal: { open: false, employee_id: '', leave_type_id: '', from_date: '', to_date: '', reason: '', attachment: null, balances: [] },
      // Reject
      rejectModal: { open: false, id: null, note: '' },
      // Employees list (admin mode only)
      allEmployees: [],
      exportOpen:         false,
      showExportModal:    false,
      pendingExportFmt:   'csv',
      selectedExportCols: [],
      exportLoading:      false,
      exportColumnDefs: [
        { key: 'employee',   label: 'Employee',   pdfWidth: 110 },
        { key: 'leave_type', label: 'Leave Type', pdfWidth: 100 },
        { key: 'from_date',  label: 'From',       pdfWidth: 90 },
        { key: 'to_date',    label: 'To',         pdfWidth: 90 },
        { key: 'days',       label: 'Days',       pdfWidth: 50 },
        { key: 'status',     label: 'Status',     pdfWidth: 80 },
        { key: 'applied_on', label: 'Applied On', pdfWidth: 90 },
      ],
    };
  },
  computed: {
    pageRange() {
      if (!this.pagination) return [];
      const cur = this.pagination.current_page, last = this.pagination.last_page;
      const pages = [];
      for (let p = Math.max(1, cur - 2); p <= Math.min(last, cur + 2); p++) pages.push(p);
      return pages;
    },
    isSelfService() {
      return this.$route?.query?.context === 'self-service';
    },
    employeeId() {
      return this.$store?.state?.getLoggedInUser?.employee_id || null;
    },
    canCancelLeave() {
      const p = this.$store?.state?.getCurrentUserPermission;
      return !this.isSelfService && !!(p?.hr_module || p?.administration_module);
    },
  },
  watch: {
    employeeId(val) {
      if (this.isSelfService && val) {
        this.reqFilters.employee_id = val;
        this.fetchRequests();
      }
    },
  },
  mounted() {
    this.fetchLeaveTypes();
    if (this.isSelfService && this.employeeId) {
      this.reqFilters.employee_id = this.employeeId;
    }
    this.fetchRequests();
    if (!this.isSelfService) {
      this.fetchEmployees();
    }
  },
  methods: {
    async fetchLeaveTypes() {
      this.typesLoading = true;
      try {
        const { data } = await axios.get('/hr/leave-type', { params: { length: 100 } });
        this.leaveTypes = data.data?.data || [];
      } catch (err){
        this.$toast?.error(err.response?.data?.message || 'failed fetching leave types');
      }
      finally { this.typesLoading = false; }
    },
    applyFilters() {
      this.reqFilters.page = 1;
      this.fetchRequests();
    },
    async fetchRequests() {
      // Always lock employee_id in self-service so other filters can't expose other employees' data
      if (this.isSelfService && this.employeeId) {
        this.reqFilters.employee_id = this.employeeId;
      }
      this.reqLoading = true;
      try {
        const { data } = await axios.get('/hr/leave', { params: this.reqFilters });
        this.pagination = data.data;
        this.requests = data.data?.data || [];
        this.reqMeta = { current_page: data.data?.current_page || 1, last_page: data.data?.last_page || 1 };
      } catch (err){
        this.$toast?.error(err.response?.data?.message || 'failed fetching leave');
      }
      finally { this.reqLoading = false; }
    },
    async fetchEmployees() {
      try { 
        const { data } = await axios.get('/payroll/employees/active'); this.allEmployees = data.data || []; 
      } catch (err){
        this.$toast?.error(err.response?.data?.message || 'failed fetching employees');
      }
    },
    reqPage(p) { this.reqFilters.page = p; this.fetchRequests(); },

    // Leave types
    openCreateType() {
      this.typeModal = { open: true, id: null, name: '', is_paid: true, accrual_rule: 'yearly', max_days: 20, carry_forward_limit: 0, carry_forward_deadline: '', allow_encashment: false, requires_attachment: false, attachment_required_after_days: 0, min_notice_days: 0, eligibility_months: 0 };
    },
    openEditType(lt) { this.typeModal = { open: true, ...lt }; },
    async saveType() {
      if (!this.typeModal.name) { this.$toast?.error('Name is required'); return; }
      this.saving = true;
      try {
        if (this.typeModal.id) { await axios.put(`/hr/leave-type/${this.typeModal.id}`, this.typeModal); }
        else { await axios.post('/hr/leave-type', this.typeModal); }
        this.typeModal.open = false;
        this.$toast?.success('Leave type saved');
        this.fetchLeaveTypes();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); }
      finally { this.saving = false; }
    },
    async toggleTypeStatus(lt) {
      try { await axios.put(`/hr/leave-type/${lt.id}/status`); lt.is_active = !lt.is_active; this.$toast?.success('Status updated'); }
      catch { this.$toast?.error('Failed'); }
    },
    async deleteType(lt) {
      if (!confirm(`Delete leave type "${lt.name}"?`)) return;
      try { await axios.delete(`/hr/leave-type/${lt.id}`); this.$toast?.success('Deleted'); this.fetchLeaveTypes(); }
      catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },

    // Apply
    openApplyModal() {
      const empId = this.isSelfService ? (this.employeeId || '') : '';
      this.applyModal = { open: true, employee_id: empId, leave_type_id: '', from_date: '', to_date: '', reason: '', attachment: null, balances: [] };
      if (empId) this.loadBalances();
    },
    async loadBalances() {
      if (!this.applyModal.employee_id) return;
      try {
        const { data } = await axios.get('/hr/leave/balances', { params: { employee_id: this.applyModal.employee_id } });
        this.applyModal.balances = data.data || [];
      } catch (err){
        this.$toast?.error(err.response?.data?.message || 'failed loading balances');
      }
    },
    async submitLeave() {
      if (this.isSelfService && !this.applyModal.employee_id) {
        this.$toast?.error('Your account is not linked to an employee profile. Contact HR.'); return;
      }
      if (!this.applyModal.employee_id || !this.applyModal.leave_type_id || !this.applyModal.from_date || !this.applyModal.to_date) {
        this.$toast?.error('Leave type and dates are required'); return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append('employee_id', this.applyModal.employee_id);
        fd.append('leave_type_id', this.applyModal.leave_type_id);
        fd.append('from_date', this.applyModal.from_date);
        fd.append('to_date', this.applyModal.to_date);
        if (this.applyModal.reason) fd.append('reason', this.applyModal.reason);
        if (this.applyModal.attachment) fd.append('attachment', this.applyModal.attachment);
        await axios.post('/hr/leave', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        this.applyModal.open = false;
        this.$toast?.success('Leave request submitted');
        this.fetchRequests();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Submission failed'); }
      finally { this.saving = false; }
    },

    // Approve / Reject / Cancel
    async approveReq(id) {
      if (!confirm('Approve this leave request?')) return;
      this.saving = true;
      try { await axios.put(`/hr/leave/${id}/approve`); this.$toast?.success('Leave approved'); this.fetchRequests(); }
      catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
      finally { this.saving = false; }
    },
    openRejectModal(req) { this.rejectModal = { open: true, id: req.id, note: '' }; },
    async submitReject() {
      this.saving = true;
      try { await axios.put(`/hr/leave/${this.rejectModal.id}/reject`, { note: this.rejectModal.note }); this.rejectModal.open = false; this.$toast?.success('Leave rejected'); this.fetchRequests(); }
      catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
      finally { this.saving = false; }
    },
    async cancelReq(id) {
      if (!confirm('Cancel this leave request?')) return;
      try { await axios.put(`/hr/leave/${id}/cancel`); this.$toast?.success('Cancelled'); this.fetchRequests(); }
      catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
    },
    openReqDetail(req) { this.$router.push(this.$url ? this.$url(`/leave/${req.id}`) : `/leave/${req.id}`); },

    // Utils
    reqStatusClass(s) { return { pending: 'chip--amber', approved: 'chip--green', rejected: 'chip--red', cancelled: 'chip--gray' }[s] || 'chip--gray'; },
    capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : ''; },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },

    openExportModal(fmt) {
      this.pendingExportFmt   = fmt;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getLeaveVal(req, key) {
      switch (key) {
        case 'employee':   return `${req.employee?.first_name ?? ''} ${req.employee?.last_name ?? ''}`.trim() || '—';
        case 'leave_type': return req.leave_type?.name || '—';
        case 'from_date':  return this.fmtDate(req.from_date);
        case 'to_date':    return this.fmtDate(req.to_date);
        case 'days':       return req.days ?? '—';
        case 'status':     return this.capitalize(req.status);
        case 'applied_on': return this.fmtDate(req.created_at);
        default: return '—';
      }
    },
    async doExport() {
      this.exportLoading = true;
      try {
        const params = { ...this.reqFilters, page: 1, length: 10000 };
        const { data } = await axios.get('/hr/leave', { params });
        const rows = data.data?.data || [];
        const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        if (this.pendingExportFmt === 'csv') {
          const header = cols.map(c => `"${c.label}"`).join(',');
          const body   = rows.map(r => cols.map(c => `"${String(this.getLeaveVal(r, c.key)).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'leave-requests.csv'; a.click(); URL.revokeObjectURL(url);
        } else {
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable = 761;
          const totalW = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale  = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.autoTable({
            head: [cols.map(c => c.label)],
            body: rows.map(r => cols.map(c => this.getLeaveVal(r, c.key))),
            startY: 40, margin: { left: 20, right: 20 }, tableWidth: usable,
            styles: { fontSize: 8, cellPadding: 4, overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold' },
            columnStyles: colStyles,
          });
          doc.save('leave-requests.pdf');
        }
        this.showExportModal = false;
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Export failed'); }
      finally { this.exportLoading = false; }
    },
    leaveNotStarted(req) {
      if (!req.from_date) return false;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const start = new Date(req.from_date); start.setHours(0, 0, 0, 0);
      return start > today;
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

.section-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }

.filter-bar { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.filter-input,.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline   { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-outline:hover,.btn-active { background:#f0f7ff; border-color:#004AAD; color:#004AAD; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:13px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover { color:#dc2626; }
.btn-approve { background:#dcfce7; color:#16a34a; border:none; border-radius:6px; padding:5px 10px; cursor:pointer; font-size:13px; }
.btn-approve:hover { background:#bbf7d0; }
.btn-reject  { background:#fee2e2; color:#dc2626; border:none; border-radius:6px; padding:5px 10px; cursor:pointer; font-size:13px; }
.btn-reject:hover { background:#fecaca; }
.btn-reject-action { background:#dc2626; color:#fff; border:none; border-radius:8px; padding:0 18px; height:38px; font-size:13px; font-weight:600; cursor:pointer; }
.btn-reject-action:disabled { opacity:.6; cursor:not-allowed; }

.table-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.empty-cell  { text-align:center; color:#aab8cc; padding:40px !important; }
.fw-600 { font-weight:600; }

.emp-cell { display:flex; flex-direction:column; gap:1px; }
.emp-name { font-weight:600; font-size:13px; color:#1a2b45; }
.emp-no   { font-size:11px; color:#7a90b0; }

.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

/* ── Per-page + Pagination ─────────────────────────────── */
.per-page-wrap    { display:flex; align-items:center; gap:6px; }
.per-page-label   { font-size:12px; color:#7a90b0; white-space:nowrap; }
.per-page-select  { height:38px; padding:0 10px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }
.pagination-bar   { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:14px 16px; border-top:1px solid #f0f4f8; }
.pagination-info  { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { background:#fff; border:1px solid #dde5f0; border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#1a2b45; }
.pg-btn:hover:not(:disabled) { background:#f0f7ff; border-color:#004AAD; color:#004AAD; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; align-items:center; gap:2px; }
.pg-num   { background:#fff; border:1px solid #dde5f0; border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#1a2b45; }
.pg-num:hover    { background:#f0f7ff; border-color:#004AAD; color:#004AAD; }
.pg-num--active  { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:700; }

/* Balance pills */
.balance-pills { display:flex; flex-wrap:wrap; gap:8px; }
.balance-pill  { border:1px solid #dde5f0; border-radius:8px; padding:6px 12px; font-size:12px; display:flex; flex-direction:column; gap:2px; background:#f8fafc; }
.balance-pill--active { border-color:#004AAD; background:#e0efff; }
.b-name { font-weight:600; color:#1a2b45; }
.b-val  { color:#004AAD; font-weight:700; }

/* Skeleton */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:48px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:720px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }
.form-cols     { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-row      { display:flex; flex-direction:column; gap:5px; }
.form-row--check { flex-direction:row; align-items:center; }
.form-label    { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input    { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.check-label   { display:flex; align-items:center; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; }
.req { color:#dc2626; }
.me-1 { margin-right:4px; }

/* Self-service */
.ss-no-profile { display:flex; align-items:center; gap:8px; background:#fef9c3; border:1px solid #fde047; border-radius:8px; padding:10px 14px; font-size:13px; color:#854d0e; font-weight:500; }

.btn-cancel-leave { display:inline-flex; align-items:center; gap:5px; background:#fff0f0; color:#dc2626; border:1px solid #fecaca; border-radius:6px; padding:4px 10px; font-size:12px; font-weight:600; cursor:pointer; }
.btn-cancel-leave:hover { background:#fee2e2; }

/* ── Export column picker ─────────────────────────────── */
.export-wrap { position:relative; display:inline-flex; }
.btn-export  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-export:hover { background:#f5f7fa; }
.export-menu { position:absolute; top:calc(100% + 4px); right:0; background:#fff; border:1px solid #dde5f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:200; min-width:160px; padding:6px; }
.export-item { display:flex; align-items:center; gap:8px; width:100%; padding:8px 12px; border:none; background:none; font-size:13px; color:#1a2b45; cursor:pointer; border-radius:6px; }
.export-item:hover { background:#f0f7ff; color:#004AAD; }
.col-picker-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
.col-picker-modal  { background:#fff; border-radius:16px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.18); }
.col-picker-header { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 0; }
.col-picker-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub    { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px 6px; flex-shrink:0; }
.col-picker-actions { display:flex; align-items:center; gap:8px; padding:14px 22px 0; }
.col-picker-link   { background:none; border:none; cursor:pointer; color:#004AAD; font-size:12px; font-weight:600; padding:0; }
.col-picker-sep    { color:#dde5f0; }
.col-picker-count  { margin-left:auto; font-size:12px; color:#7a90b0; }
.col-picker-grid   { display:grid; grid-template-columns:1fr 1fr; gap:4px; padding:14px 22px; }
.col-picker-item   { display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:8px; cursor:pointer; border:1px solid #f0f4f8; }
.col-picker-item:hover { background:#f0f7ff; border-color:#c7d8ef; }
.col-picker-check  { cursor:pointer; accent-color:#004AAD; width:15px; height:15px; flex-shrink:0; }
.col-picker-label  { font-size:13px; color:#1a2b45; font-weight:500; }
.col-picker-footer { display:flex; justify-content:flex-end; gap:8px; padding:14px 22px 20px; border-top:1px solid #f0f4f8; margin-top:8px; }
.fade-enter-active,.fade-leave-active { transition:opacity .18s; }
.fade-enter,.fade-leave-to { opacity:0; }
</style>
